import { useCallback, useEffect, useRef, useState } from 'react';
import logger, { LogCategory } from '@/lib/logging';

/**
 * - `idle`: nothing shared yet (also after the picker was cancelled or sharing was stopped)
 * - `requesting`: browser share picker is open
 * - `live`: a call window / screen / tab is being shared
 * - `ended`: the share ended outside Harry (browser "Stop sharing", window closed)
 * - `error`: the share could not start
 * - `unsupported`: this browser has no getDisplayMedia
 */
export type ScreenShareStatus = 'idle' | 'requesting' | 'live' | 'ended' | 'error' | 'unsupported';

export interface ScreenShareState {
  status: ScreenShareStatus;
  stream: MediaStream | null;
  start: () => Promise<void>;
  stop: () => void;
}

export function isScreenShareSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}

/**
 * Owns one getDisplayMedia stream (video only, no audio: the call conversation is never captured).
 * Tracks are stopped on stop() and on unmount so the practitioner's screen is never shared longer
 * than the page is open.
 */
export function useScreenShare(): ScreenShareState {
  const [status, setStatus] = useState<ScreenShareStatus>(() => (isScreenShareSupported() ? 'idle' : 'unsupported'));
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const endedRef = useRef<{ track: MediaStreamTrack; listener: () => void } | null>(null);

  const release = useCallback(() => {
    if (endedRef.current) {
      endedRef.current.track.removeEventListener('ended', endedRef.current.listener);
      endedRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  const start = useCallback(async () => {
    if (!isScreenShareSupported()) {
      setStatus('unsupported');
      return;
    }
    release();
    setStatus('requesting');
    try {
      const next = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      streamRef.current = next;
      setStream(next);
      setStatus('live');
      logger.info('RemoteAssessment: screen share started', {
        category: LogCategory.CAMERA,
        screen: true,
        trackCount: next.getTracks().length,
      });
      const [track] = next.getVideoTracks();
      if (track) {
        const listener = () => {
          logger.debug('RemoteAssessment: screen share ended outside Harry', {
            category: LogCategory.CAMERA,
            screen: true,
          });
          release();
          setStatus('ended');
        };
        endedRef.current = { track, listener };
        track.addEventListener('ended', listener);
      }
    } catch (err) {
      const domErr = err as DOMException;
      logger.error('RemoteAssessment: screen share failed', {
        category: LogCategory.CAMERA,
        screen: true,
        errorName: domErr?.name,
        errorMessage: (err as Error)?.message,
      });
      setStatus('error');
    }
  }, [release]);

  const stop = useCallback(() => {
    release();
    setStatus(isScreenShareSupported() ? 'idle' : 'unsupported');
  }, [release]);

  useEffect(() => release, [release]);

  return { status, stream, start, stop };
}
